import AbstractTab from "./AbstractTab";
import EventTypes from "../shared/EventTypes";
import { addSvg } from "../shared/utils";

export default class PlantsTab extends AbstractTab {
    name = 'plants';
    tooltip = 'Plants (P)';
    icon = '<svg class="icon"><use xlink:href="/icons/__remixicon.sprite.svg?v=491#plant-line"></use></svg>';

    filtersOpen = false;
    textFilter = '';
    layerFilter = '';

    constructor(provider, tabContainer) {
        super(provider);

        this.plantOverview = this.p.createDiv()
            .parent(tabContainer)
            .class("planner-aside-wrapper");

        let header = this.p.createDiv()
            .parent(this.plantOverview)
            .style('display', 'flex')
            .style('align-items', 'center')
            .style('justify-content', 'space-between')
            .class('mar-b-sm');

        this.p.createElement('H3', 'Plants in this plan')
            .parent(header)
            .class('mar-t-0 mar-b-0');

        this.filterButton = this.p.createDiv()
            .parent(header)
            .class('btn-icon')
            .style('cursor', 'pointer')
            .style('width', '32px')
            .style('height', '32px')
            .attribute('data-tooltip', 'Filter')
            .mouseClicked(() => this.toggleFilters());
        addSvg(this.filterButton, 'equalizer-line');
        this.filterButton.elt.querySelector('svg').style.width = '20px';
        this.filterButton.elt.querySelector('svg').style.height = '20px';

        this.createFilterInputs();

        this.rowContainer = this.p.createDiv().parent(this.plantOverview)
            .class('pad-b');

        this.plantOverview.hide();

        this.on(EventTypes.UPDATE_LAYER_NAME, () => this.populateLayerOptions());
        this.on(EventTypes.ADD_LAYER, () => this.populateLayerOptions());
        this.on(EventTypes.REMOVE_LAYER, () => this.populateLayerOptions());
        this.on(EventTypes.TIMELINE_DATE_CHANGED, () => {
            if (this.filtersOpen && this.visibleCheckbox.checked()) this.applyFilters();
        });

        this.resizeWindow();
    }

    createFilterInputs() {
        this.filterContainer = this.p.createDiv()
            .parent(this.plantOverview)
            .style('display', 'flex')
            .style('flex-direction', 'column')
            .style('gap', '6px')
            .style('margin-bottom', '6px')
            .style('display', 'none');

        let inputWrapper = this.p.createDiv()
            .parent(this.filterContainer)
            .style('position', 'relative')
            .style('width', '100%');

        this.textInput = this.p.createInput('')
            .parent(inputWrapper)
            .class('input-text')
            .attribute('placeholder', 'Filter by name...')
            .style('width', '100%')
            .style('box-sizing', 'border-box')
            .style('padding-right', '2rem');
        this.textInput.elt.oninput = () => {
            this.textFilter = this.textInput.value();
            this.applyFilters();
        };
        this.textInput.elt.onfocus = () => this.enableHotKeys = false;
        this.textInput.elt.onblur = () => this.enableHotKeys = true;

        this.clearButton = this.p.createSpan('')
            .parent(inputWrapper)
            .style('position', 'absolute')
            .style('right', '0.5rem')
            .style('top', '50%')
            .style('transform', 'translateY(-50%)')
            .style('cursor', 'pointer')
            .style('line-height', '0');
        this.clearButton.mouseClicked(() => {
            this.textInput.value('');
            this.textFilter = '';
            this.applyFilters();
        });
        addSvg(this.clearButton, 'close-circle-fill');
        this.clearButton.elt.querySelector('svg').style.width = '16px';
        this.clearButton.elt.querySelector('svg').style.height = '16px';
        this.clearButton.elt.querySelector('svg').style.fill = '#bbb';

        this.layerSelect = this.p.createElement('select')
            .parent(this.filterContainer)
            .class('input-text');
        this.layerSelect.elt.onchange = () => {
            this.layerFilter = this.layerSelect.elt.value;
            this.applyFilters();
        };

        this.visibleCheckbox = this.p.createCheckbox('Currently visible', false)
            .parent(this.filterContainer);
        this.visibleCheckbox.elt.querySelector('input').style.marginRight = '8px';
        this.visibleCheckbox.changed(() => this.applyFilters());
    }

    populateLayerOptions() {
        let current = this.layerFilter;
        this.layerSelect.elt.innerHTML = '';

        let all = document.createElement('option');
        all.value = '';
        all.textContent = 'All layers';
        this.layerSelect.elt.appendChild(all);

        let none = document.createElement('option');
        none.value = '__none__';
        none.textContent = 'No layer';
        this.layerSelect.elt.appendChild(none);

        Object.values(this.layers.collection).forEach(layer => {
            let option = document.createElement('option');
            option.value = layer.id;
            option.textContent = layer.name;
            this.layerSelect.elt.appendChild(option);
        });

        if (current && (current === '__none__' || this.layers.get(current))) {
            this.layerSelect.elt.value = current;
        } else {
            this.layerFilter = '';
            this.layerSelect.elt.value = '';
        }
    }

    toggleFilters() {
        this.filtersOpen = !this.filtersOpen;
        if (this.filtersOpen) {
            this.populateLayerOptions();
            this.textInput.value(this.textFilter);
            this.layerSelect.elt.value = this.layerFilter;
            this.filterContainer.style('display', 'flex');
            this.applyFilters();
        } else {
            this.filterContainer.style('display', 'none');
            this.showAll();
        }
    }

    applyFilters() {
        let text = this.textFilter.toLowerCase();
        let onlyVisible = this.visibleCheckbox.checked();
        Object.entries(this.speciesCollection.rows).forEach(([speciesId, row]) => {
            let species = this.speciesCollection.get(speciesId);
            let elements = this.speciesCollection.speciesElements[speciesId] || [];

            let matchText = !text
                || species.name.toLowerCase().includes(text)
                || species.scientific_name.toLowerCase().includes(text)
                || (species.parent_name && species.parent_name.toLowerCase().includes(text));

            let matchLayer = !this.layerFilter;
            if (!matchLayer) {
                if (this.layerFilter === '__none__') {
                    matchLayer = elements.some(el => !el.layer);
                } else {
                    matchLayer = elements.some(el => el.layer && el.layer.id === this.layerFilter);
                }
            }

            let matchVisible = !onlyVisible || elements.some(el => el.currentlyVisible());

            // Only show rows that have elements (visible in the plan) and match filters
            let hasElements = elements.length > 0;
            hasElements && matchText && matchLayer && matchVisible ? row.show() : row.hide();
        });
    }

    showAll() {
        Object.entries(this.speciesCollection.rows).forEach(([speciesId, row]) => {
            let elements = this.speciesCollection.speciesElements[speciesId] || [];
            elements.length > 0 ? row.show() : row.hide();
        });
    }

    positionPlantOverview() {
        // this.plantOverview.position(this.sideBar.x, 0);
    }

    resizeWindow() {
        // this.positionPlantOverview();
    }

    activate() {
        // this.button.addClass('-is-active');
        this.plantOverview.show();
        if (this.filtersOpen) {
            this.applyFilters();
        }
    }

    deactivate() {
        // this.button.removeClass('-is-active');
        this.plantOverview.hide();
    }
}
